import { DefaultFetchService } from "../fetchService";
import type { GetRequestOptions, PostConfigObject } from "../fetchService";

/** Both stream endpoints are requested with `withFullResponse`, so only the raw `Response`
 *  (the SSE body the caller parses itself) comes back — never a parsed result. */
type RawStreamResponse = { response: Response };

const BASE_PATH = "sse/custom-industry-agent";

type CustomIndustryRunPayload = {
    countryId: number;
    description: string;
    /** How many segments the caller may still create; omitted leaves the run uncapped. */
    maxSegments?: number;
};

type CustomIndustryThreadResponse = {
    threadId: string;
};

type CustomIndustryFailure = {
    domain: string;
    error: string;
};

type CustomIndustryResult = {
    /**
     * False once the run has given up without an industry, absent while it has not decided.
     * The agent writes this only to report failure, so `undefined` means "still working" --
     * which is also what an older Pro answers during a rolling deploy. Compare against
     * `false` explicitly; never treat a missing value as a failure.
     */
    success?: boolean;
    /** Empty until the run has created the industry. */
    industryId: string;
    industryName: string;
    /**
     * Why the agent rejected the prompt (e.g. `"no_domains"`), so the user should rephrase it.
     * Absent or empty otherwise -- including a run that failed internally (`success: false`
     * with no code), which is not the prompt's fault. The endpoint drops nulls, and an older
     * Pro never sends it.
     */
    errorCode?: string;
    topic: string;
    domainCount: number;
    segmentCount: number;
    /**
     * Domains that needed a segment but did not fit the run's segment budget. Optional so a
     * client that reaches an older Pro during a rolling deploy still parses the result.
     */
    segmentsOverBudgetCount?: number;
    failures: CustomIndustryFailure[];
    /**
     * True while the run is still working, e.g. warming the overview after the industry was
     * created. An older Pro never sends it, so treat a missing value as not running.
     */
    isRunning?: boolean;
    /**
     * True while the run only warms what the Players page reads, its industry and overview
     * already ready: hand the industry over instead of waiting for `isRunning` to clear. An
     * older Pro never sends it.
     */
    overviewReady?: boolean;
};

const createCustomIndustryAgentService = () => {
    const fetchService = DefaultFetchService.getInstance();

    const createThread = (): Promise<CustomIndustryThreadResponse> => fetchService.post(`${BASE_PATH}/thread`);

    const runAgent = (
        threadId: string,
        payload: CustomIndustryRunPayload,
        config?: Pick<PostConfigObject, "cancellation">,
    ): Promise<RawStreamResponse> =>
        fetchService.post<RawStreamResponse>(`${BASE_PATH}/run?threadId=${encodeURIComponent(threadId)}`, payload, {
            // The response is an SSE stream — parsing it as JSON would consume the body.
            isRawResponse: true,
            withFullResponse: true,
            cancellation: config?.cancellation,
        });

    // What a finished run produced. Creates nothing — the agent already created the
    // assets — so this is safe to call again when the stream drops.
    const result = (threadId: string): Promise<CustomIndustryResult> =>
        fetchService.get(`${BASE_PATH}/result`, { threadId });

    const attachStream = (
        threadId: string,
        runId: string,
        config?: Pick<GetRequestOptions, "cancellation">,
    ): Promise<RawStreamResponse> =>
        fetchService.get<RawStreamResponse>(
            `${BASE_PATH}/stream`,
            { threadId, runId },
            {
                isRawResponse: true,
                withFullResponse: true,
                preventAutoCancellation: true,
                cancellation: config?.cancellation,
            },
        );

    const cancel = (threadId: string, runId?: string | null): Promise<void> => {
        const query = new URLSearchParams({ threadId });
        if (runId) {
            query.set("runId", runId);
        }

        return fetchService.post(`${BASE_PATH}/cancel?${query.toString()}`);
    };

    return { createThread, runAgent, attachStream, result, cancel };
};

const customIndustryAgentService = createCustomIndustryAgentService();

export default customIndustryAgentService;
export type {
    CustomIndustryFailure,
    CustomIndustryResult,
    CustomIndustryRunPayload,
    CustomIndustryThreadResponse,
    RawStreamResponse,
};
